import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Shield,
  MagnifyingGlass,
  Link as LinkIcon,
  ClockCounterClockwise,
  Lightbulb,
  House,
  UploadSimple,
  Warning,
  CheckCircle,
  XCircle,
  Trash,
  CaretRight,
  CircleNotch,
  ShieldCheck,
  ShieldWarning,
  Globe,
  File,
  Info,
  Key,
  EnvelopeSimple,
  MapPin,
  QrCode,
  Hash,
  LinkBreak,
  Desktop,
  Browser,
  CloudArrowUp,
  Lock,
  Users,
  ChartBar,
  DeviceMobile,
  SignOut,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Access Gate Component
const AccessGate = ({ onAccessGranted }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const verifyCode = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/auth/verify-code`, { code });
      if (response.data.valid) {
        // Store in session
        sessionStorage.setItem("av_access", JSON.stringify({
          role: response.data.role,
          code: code
        }));
        onAccessGranted(response.data.role, code);
      } else {
        setError("Invalid access code");
      }
    } catch (err) {
      setError("Failed to verify code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#007AFF]/20 rounded-full mb-6">
            <Shield size={48} weight="duotone" className="text-[#007AFF]" />
          </div>
          <h1 className="font-heading font-black text-4xl text-white mb-2">AVShield</h1>
          <p className="text-white/60">Advanced Security Scanner</p>
        </div>

        <div className="card p-8">
          <div className="text-center mb-6">
            <Lock size={32} className="text-white/40 mx-auto mb-3" />
            <h2 className="font-bold text-xl text-white mb-2">Enter Access Code</h2>
            <p className="text-white/60 text-sm">Enter your access code to continue</p>
          </div>

          <div className="space-y-4">
            <div className="relative">
              <Key size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="password"
                data-testid="access-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && verifyCode()}
                placeholder="Enter access code"
                className="input-field pl-12 text-center text-2xl tracking-widest"
                maxLength={10}
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-[#FF3B30] text-sm justify-center">
                <XCircle size={16} />
                {error}
              </div>
            )}

            <button
              data-testid="verify-code-btn"
              onClick={verifyCode}
              disabled={loading || !code.trim()}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <CircleNotch size={20} className="spinner" /> : <ShieldCheck size={20} />}
              Unlock Access
            </button>
          </div>
        </div>

        <p className="text-center text-white/40 text-xs mt-6">
          Protected by AVShield Security
        </p>
      </div>
    </div>
  );
};

// Download Options Component
const DownloadOptions = ({ onSelect, onBack }) => {
  const [registering, setRegistering] = useState(false);

  const registerAndProceed = async (type) => {
    setRegistering(true);
    try {
      const deviceInfo = {
        device_name: navigator.userAgent.includes("Windows") ? "Windows PC" : 
                     navigator.userAgent.includes("Mac") ? "Mac" :
                     navigator.userAgent.includes("Linux") ? "Linux PC" : "Unknown Device",
        device_type: type,
        os_info: navigator.userAgent,
        ip_address: null
      };
      
      await axios.post(`${API}/devices/register`, deviceInfo);
    } catch (err) {
      console.error("Failed to register device:", err);
    } finally {
      setRegistering(false);
      onSelect(type);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          <h1 className="font-heading font-black text-4xl text-white mb-2">Choose Your Protection</h1>
          <p className="text-white/60">Select how you want to use AVShield</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Desktop App */}
          <div className="card p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#007AFF]/20 rounded-full mb-4">
              <Desktop size={32} className="text-[#007AFF]" />
            </div>
            <h3 className="font-bold text-xl text-white mb-2">Desktop App</h3>
            <p className="text-white/60 text-sm mb-4">
              Download Python app for Windows, Mac, Linux
            </p>
            <a 
              href={`${API}/download/desktop-app`}
              download="AVShield-Desktop.zip"
              onClick={() => registerAndProceed("desktop")}
              className="inline-block btn-primary px-6 py-3 rounded-lg text-center"
            >
              Download ZIP
            </a>
          </div>

          {/* Browser Extension */}
          <div className="card p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#5856D6]/20 rounded-full mb-4">
              <Browser size={32} className="text-[#5856D6]" />
            </div>
            <h3 className="font-bold text-xl text-white mb-2">Browser Extension</h3>
            <p className="text-white/60 text-sm mb-4">
              Chrome/Brave/Edge - right-click scan any link
            </p>
            <a 
              href={`${API}/download/browser-extension`}
              download="AVShield-Extension.zip"
              onClick={() => registerAndProceed("browser_extension")}
              className="inline-block bg-[#5856D6] text-white px-6 py-3 rounded-lg text-center hover:bg-[#4745B5]"
            >
              Download ZIP
            </a>
          </div>

          {/* Web Scanner */}
          <div 
            className="card p-8 text-center cursor-pointer hover:border-[#FF9500] transition-all group"
            onClick={() => !registering && registerAndProceed("web")}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF9500]/20 rounded-full mb-4 group-hover:bg-[#FF9500]/30 transition-all">
              <CloudArrowUp size={32} className="text-[#FF9500]" />
            </div>
            <h3 className="font-bold text-xl text-white mb-2">Web Scanner</h3>
            <p className="text-white/60 text-sm mb-4">
              Upload files & check URLs directly in browser
            </p>
            <span className="inline-block bg-[#FF9500] text-white px-6 py-3 rounded-lg">
              Use Now
            </span>
          </div>
        </div>

        {registering && (
          <div className="text-center mt-6">
            <CircleNotch size={24} className="spinner text-[#007AFF] mx-auto" />
          </div>
        )}

        <button onClick={onBack} className="mt-8 text-white/40 hover:text-white text-sm mx-auto block">
          ← Back to access code
        </button>
      </div>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = ({ adminCode, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, devicesRes] = await Promise.all([
        axios.get(`${API}/admin/stats?admin_code=${adminCode}`),
        axios.get(`${API}/admin/devices?admin_code=${adminCode}`)
      ]);
      setStats(statsRes.data);
      setDevices(devicesRes.data.devices || []);
    } catch (err) {
      console.error("Failed to fetch admin data:", err);
    } finally {
      setLoading(false);
    }
  }, [adminCode]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchData]);

  const deleteDevice = async (deviceId) => {
    try {
      await axios.delete(`${API}/admin/devices/${deviceId}?admin_code=${adminCode}`);
      setDevices(devices.filter(d => d.id !== deviceId));
    } catch (err) {
      console.error("Failed to delete device:", err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <CircleNotch size={48} className="spinner text-[#007AFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-black text-3xl text-white mb-1">Admin Dashboard</h1>
            <p className="text-white/60">Monitor your AVShield deployment</p>
          </div>
          <button onClick={onLogout} className="flex items-center gap-2 text-white/60 hover:text-white">
            <SignOut size={20} />
            Logout
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="stats-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-white/60 mb-2">Total Devices</p>
                <p className="stats-value text-[#007AFF]">{stats?.devices?.total || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-[#007AFF]/20">
                <Users size={24} className="text-[#007AFF]" />
              </div>
            </div>
          </div>

          <div className="stats-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-white/60 mb-2">Desktop Apps</p>
                <p className="stats-value text-[#39FF14]">{stats?.devices?.desktop || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-[#39FF14]/20">
                <Desktop size={24} className="text-[#39FF14]" />
              </div>
            </div>
          </div>

          <div className="stats-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-white/60 mb-2">Extensions</p>
                <p className="stats-value text-[#5856D6]">{stats?.devices?.browser_extension || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-[#5856D6]/20">
                <Browser size={24} className="text-[#5856D6]" />
              </div>
            </div>
          </div>

          <div className="stats-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-white/60 mb-2">Total Scans</p>
                <p className="stats-value text-[#FF9500]">{stats?.scans?.total || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-[#FF9500]/20">
                <ChartBar size={24} className="text-[#FF9500]" />
              </div>
            </div>
          </div>
        </div>

        {/* Devices Table */}
        <div className="card p-6">
          <h2 className="font-bold text-xl text-white mb-4">Registered Devices ({devices.length})</h2>
          
          {devices.length === 0 ? (
            <div className="text-center py-12">
              <DeviceMobile size={48} className="text-white/20 mx-auto mb-4" />
              <p className="text-white/60">No devices registered yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Type</th>
                    <th>OS</th>
                    <th>Registered</th>
                    <th>Last Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device) => (
                    <tr key={device.id}>
                      <td className="text-white">{device.device_name}</td>
                      <td>
                        <span className={`badge ${
                          device.device_type === "desktop" ? "bg-[#39FF14]/20 text-[#39FF14]" :
                          device.device_type === "browser_extension" ? "bg-[#5856D6]/20 text-[#5856D6]" :
                          "bg-[#FF9500]/20 text-[#FF9500]"
                        }`}>
                          {device.device_type?.toUpperCase().replace("_", " ")}
                        </span>
                      </td>
                      <td className="text-white/60 text-xs max-w-[200px] truncate">{device.os_info?.substring(0, 50)}...</td>
                      <td className="text-white/60 text-sm">{new Date(device.registered_at).toLocaleDateString()}</td>
                      <td className="text-white/60 text-sm">{new Date(device.last_active).toLocaleString()}</td>
                      <td>
                        <button 
                          onClick={() => deleteDevice(device.id)}
                          className="p-2 hover:bg-[#FF3B30]/20 rounded transition-colors"
                        >
                          <Trash size={16} className="text-[#FF3B30]" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Sidebar Navigation Component
const Sidebar = ({ activeTab, setActiveTab, onLogout }) => {
  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: House },
    { id: "file-scan", label: "File Scanner", icon: MagnifyingGlass },
    { id: "url-scan", label: "URL Checker", icon: LinkIcon },
    { id: "password-check", label: "Password Check", icon: Key },
    { id: "ip-lookup", label: "IP Lookup", icon: MapPin },
    { id: "link-expander", label: "Link Expander", icon: LinkBreak },
    { id: "hash-lookup", label: "Hash Lookup", icon: Hash },
    { id: "email-analyzer", label: "Email Analyzer", icon: EnvelopeSimple },
    { id: "qr-scanner", label: "QR Scanner", icon: QrCode },
    { id: "history", label: "Scan History", icon: ClockCounterClockwise },
    { id: "tips", label: "Security Tips", icon: Lightbulb },
  ];

  return (
    <aside className="w-64 h-screen bg-[#121212] border-r border-white/10 flex flex-col fixed left-0 top-0 overflow-y-auto">
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <Shield size={32} weight="duotone" className="text-[#007AFF]" />
          <div>
            <h1 className="font-heading font-black text-lg text-white">AVShield</h1>
            <p className="text-xs text-white/60">Security Scanner</p>
          </div>
        </div>
      </div>
      
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => (
          <button
            key={item.id}
            data-testid={`nav-${item.id}`}
            onClick={() => setActiveTab(item.id)}
            className={`nav-item w-full ${activeTab === item.id ? "active" : ""}`}
          >
            <item.icon size={20} weight="duotone" />
            <span className="font-medium text-sm">{item.label}</span>
          </button>
        ))}
      </nav>
      
      <div className="p-4 border-t border-white/10">
        <button 
          onClick={onLogout}
          className="nav-item w-full text-[#FF3B30] hover:bg-[#FF3B30]/10"
        >
          <SignOut size={20} />
          <span className="font-medium text-sm">Logout</span>
        </button>
      </div>
    </aside>
  );
};

// Threat Level Badge Component
const ThreatBadge = ({ level }) => {
  const styles = {
    critical: "threat-critical",
    high: "threat-high",
    medium: "threat-medium",
    low: "threat-low",
    clean: "threat-clean",
    unknown: "threat-unknown",
  };

  const labels = {
    critical: "CRITICAL",
    high: "HIGH RISK",
    medium: "MEDIUM",
    low: "LOW RISK",
    clean: "CLEAN",
    unknown: "UNKNOWN",
  };

  return (
    <span className={`badge ${styles[level] || styles.unknown}`}>
      {labels[level] || "UNKNOWN"}
    </span>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color = "#007AFF" }) => (
  <div className="stats-card">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-widest text-white/60 mb-2">{title}</p>
        <p className="stats-value" style={{ color }}>{value}</p>
      </div>
      <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
        <Icon size={24} weight="duotone" style={{ color }} />
      </div>
    </div>
  </div>
);

// Dashboard Component
const Dashboard = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={48} className="spinner text-[#007AFF]" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-view" className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading font-black text-3xl text-white mb-2">Security Dashboard</h2>
        <p className="text-white/60">Monitor your security scans and threat detections</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard title="Total Scans" value={stats.total_scans || 0} icon={Shield} color="#007AFF" />
        <StatsCard title="Files Scanned" value={stats.file_scans || 0} icon={File} color="#34C759" />
        <StatsCard title="URLs Checked" value={stats.url_scans || 0} icon={Globe} color="#5856D6" />
        <StatsCard title="Threats Found" value={stats.threats_found || 0} icon={Warning} color="#FF3B30" />
      </div>

      <div className="card p-6">
        <h3 className="font-heading font-bold text-xl text-white mb-4">Threat Distribution</h3>
        <div className="grid grid-cols-5 gap-4">
          {Object.entries(stats.threat_distribution || {}).map(([level, count]) => (
            <div key={level} className="text-center">
              <ThreatBadge level={level} />
              <p className="mt-2 text-2xl font-bold text-white">{count}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="card p-6">
        <h3 className="font-heading font-bold text-xl text-white mb-4">Recent Scans</h3>
        {stats.recent_scans?.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_scans.map((scan) => (
              <div key={scan.id} className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg">
                <div className="flex items-center gap-3">
                  {scan.scan_type === "file" ? <File size={20} className="text-white/60" /> : <Globe size={20} className="text-white/60" />}
                  <span className="text-white truncate max-w-[300px]">{scan.target}</span>
                </div>
                <ThreatBadge level={scan.threat_level} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-white/60 text-center py-8">No scans yet. Start scanning to see results.</p>
        )}
      </div>
    </div>
  );
};

// File Scanner Component
const FileScanner = ({ onScanComplete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) scanFile(e.dataTransfer.files[0]);
  };

  const handleFileSelect = (e) => {
    if (e.target.files.length > 0) scanFile(e.target.files[0]);
  };

  const scanFile = async (file) => {
    setScanning(true);
    setResult(null);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/scan/file`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(response.data);
      onScanComplete();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to scan file");
    } finally {
      setScanning(false);
    }
  };

  return (
    <div data-testid="file-scanner-view" className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading font-black text-3xl text-white mb-2">File Scanner</h2>
        <p className="text-white/60">Upload files to scan for malware and viruses</p>
      </div>

      <div
        data-testid="file-dropzone"
        className={`dropzone p-12 text-center ${isDragging ? "active" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input type="file" id="file-input" data-testid="file-input" className="hidden" onChange={handleFileSelect} disabled={scanning} />
        {scanning ? (
          <div className="space-y-4">
            <CircleNotch size={64} className="spinner text-[#007AFF] mx-auto" />
            <p className="text-white text-lg">Scanning file...</p>
          </div>
        ) : (
          <label htmlFor="file-input" className="cursor-pointer">
            <UploadSimple size={64} className="text-white/40 mx-auto mb-4" />
            <p className="text-white text-lg mb-2">Drag & drop a file here, or <span className="text-[#007AFF]">browse</span></p>
            <p className="text-white/40 text-sm">Maximum file size: 32MB</p>
          </label>
        )}
      </div>

      {error && (
        <div className="card p-4 border-[#FF3B30] bg-[#FF3B30]/10">
          <div className="flex items-center gap-3">
            <XCircle size={24} className="text-[#FF3B30]" />
            <span className="text-[#FF3B30]">{error}</span>
          </div>
        </div>
      )}

      {result && <ScanResultCard result={result} />}
    </div>
  );
};

// URL Scanner Component
const URLScanner = ({ onScanComplete }) => {
  const [url, setUrl] = useState("");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const scanUrl = async () => {
    if (!url.trim()) return;
    setScanning(true);
    setResult(null);
    setError(null);
    try {
      const response = await axios.post(`${API}/scan/url`, { url });
      setResult(response.data);
      onScanComplete();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to scan URL");
    } finally {
      setScanning(false);
    }
  };

  return (
    <div data-testid="url-scanner-view" className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading font-black text-3xl text-white mb-2">URL Safety Checker</h2>
        <p className="text-white/60">Check if a website is safe before visiting</p>
      </div>

      <div className="card p-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Globe size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="url"
              data-testid="url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && scanUrl()}
              placeholder="https://example.com"
              className="input-field pl-12"
              disabled={scanning}
            />
          </div>
          <button data-testid="scan-url-btn" onClick={scanUrl} disabled={scanning || !url.trim()} className="btn-primary flex items-center gap-2 disabled:opacity-50">
            {scanning ? <CircleNotch size={20} className="spinner" /> : <ShieldCheck size={20} />}
            Scan URL
          </button>
        </div>
      </div>

      {error && (
        <div className="card p-4 border-[#FF3B30] bg-[#FF3B30]/10">
          <div className="flex items-center gap-3"><XCircle size={24} className="text-[#FF3B30]" /><span className="text-[#FF3B30]">{error}</span></div>
        </div>
      )}

      {result && <ScanResultCard result={result} />}
    </div>
  );
};

// Scan Result Card Component
const ScanResultCard = ({ result }) => {
  const isThreat = ["critical", "high", "medium"].includes(result.threat_level);
  const total = (result.malicious_count || 0) + (result.harmless_count || 0) + (result.suspicious_count || 0) + (result.undetected_count || 0);

  return (
    <div data-testid="scan-result" className={`card p-6 ${isThreat ? "glow-danger border-[#FF3B30]" : "glow-success border-[#39FF14]"}`}>
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          {isThreat ? <ShieldWarning size={48} weight="duotone" className="text-[#FF3B30]" /> : <ShieldCheck size={48} weight="duotone" className="text-[#39FF14]" />}
          <div>
            <h3 className="font-heading font-bold text-xl text-white mb-1">{result.target}</h3>
            <ThreatBadge level={result.threat_level} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="text-center p-4 bg-[#FF3B30]/10 rounded-lg">
          <p className="text-2xl font-bold text-[#FF3B30]">{result.malicious_count || 0}</p>
          <p className="text-xs text-white/60 uppercase tracking-wider">Malicious</p>
        </div>
        <div className="text-center p-4 bg-[#FFCC00]/10 rounded-lg">
          <p className="text-2xl font-bold text-[#FFCC00]">{result.suspicious_count || 0}</p>
          <p className="text-xs text-white/60 uppercase tracking-wider">Suspicious</p>
        </div>
        <div className="text-center p-4 bg-[#39FF14]/10 rounded-lg">
          <p className="text-2xl font-bold text-[#39FF14]">{result.harmless_count || 0}</p>
          <p className="text-xs text-white/60 uppercase tracking-wider">Harmless</p>
        </div>
        <div className="text-center p-4 bg-white/5 rounded-lg">
          <p className="text-2xl font-bold text-white/60">{result.undetected_count || 0}</p>
          <p className="text-xs text-white/60 uppercase tracking-wider">Undetected</p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex justify-between text-xs text-white/60 mb-2">
          <span>Detection Rate</span>
          <span>{total > 0 ? Math.round(((result.malicious_count || 0) / total) * 100) : 0}% of {total} engines</span>
        </div>
        <div className="h-3 bg-black rounded-full overflow-hidden flex">
          <div className="h-full bg-[#FF3B30]" style={{ width: `${total > 0 ? ((result.malicious_count || 0) / total) * 100 : 0}%` }} />
          <div className="h-full bg-[#FFCC00]" style={{ width: `${total > 0 ? ((result.suspicious_count || 0) / total) * 100 : 0}%` }} />
          <div className="h-full bg-[#39FF14]" style={{ width: `${total > 0 ? ((result.harmless_count || 0) / total) * 100 : 0}%` }} />
        </div>
      </div>

      {result.engine_results?.length > 0 && (
        <div>
          <h4 className="font-bold text-white mb-3">Detections ({result.engine_results.length})</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {result.engine_results.map((engine, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg">
                <span className="text-white font-medium">{engine.engine}</span>
                <span className="text-[#FF3B30] text-sm">{engine.result}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Password Checker Component
const PasswordChecker = () => {
  const [password, setPassword] = useState("");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const checkPassword = async () => {
    if (!password) return;
    setChecking(true);
    try {
      const response = await axios.post(`${API}/security/password-check`, { password });
      setResult(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setChecking(false);
    }
  };

  const strengthColors = { weak: "#FF3B30", medium: "#FFCC00", strong: "#39FF14" };

  return (
    <div data-testid="password-checker-view" className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading font-black text-3xl text-white mb-2">Password Strength Checker</h2>
        <p className="text-white/60">Check if your password is strong and hasn't been leaked</p>
      </div>

      <div className="card p-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Key size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type={showPassword ? "text" : "password"}
              data-testid="password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && checkPassword()}
              placeholder="Enter password to check"
              className="input-field pl-12 pr-24"
            />
            <button onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white text-sm">
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <button data-testid="check-password-btn" onClick={checkPassword} disabled={checking || !password} className="btn-primary flex items-center gap-2 disabled:opacity-50">
            {checking ? <CircleNotch size={20} className="spinner" /> : <ShieldCheck size={20} />}
            Check
          </button>
        </div>
      </div>

      {result && (
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-4 rounded-lg" style={{ backgroundColor: `${strengthColors[result.strength]}20` }}>
              {result.strength === "strong" ? <ShieldCheck size={32} style={{ color: strengthColors[result.strength] }} /> : <ShieldWarning size={32} style={{ color: strengthColors[result.strength] }} />}
            </div>
            <div>
              <h3 className="font-heading font-bold text-xl text-white capitalize">{result.strength} Password</h3>
              <p className="text-white/60">Score: {result.score}/{result.max_score}</p>
            </div>
          </div>

          <div className="mb-6">
            <div className="h-3 bg-black rounded-full overflow-hidden">
              <div className="h-full transition-all duration-300" style={{ width: `${(result.score / result.max_score) * 100}%`, backgroundColor: strengthColors[result.strength] }} />
            </div>
          </div>

          {result.feedback?.length > 0 && (
            <div className="mb-6">
              <h4 className="font-bold text-white mb-3">Suggestions</h4>
              <ul className="space-y-2">
                {result.feedback.map((tip, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-white/60">
                    <Warning size={16} className="text-[#FFCC00]" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={`p-4 rounded-lg ${result.breached ? "bg-[#FF3B30]/10 border border-[#FF3B30]" : "bg-[#39FF14]/10 border border-[#39FF14]"}`}>
            <div className="flex items-center gap-3">
              {result.breached ? <XCircle size={24} className="text-[#FF3B30]" /> : <CheckCircle size={24} className="text-[#39FF14]" />}
              <div>
                <p className={result.breached ? "text-[#FF3B30] font-bold" : "text-[#39FF14] font-bold"}>
                  {result.breached ? "Password Found in Data Breaches!" : "Not Found in Known Breaches"}
                </p>
                <p className="text-white/60 text-sm">{result.message}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// IP Lookup Component
const IPLookup = () => {
  const [ip, setIp] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const lookupIP = async () => {
    if (!ip.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/security/ip-lookup`, { ip_address: ip });
      setResult(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="ip-lookup-view" className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading font-black text-3xl text-white mb-2">IP Address Lookup</h2>
        <p className="text-white/60">Get geolocation and threat info for any IP address</p>
      </div>

      <div className="card p-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <MapPin size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input type="text" data-testid="ip-input" value={ip} onChange={(e) => setIp(e.target.value)} onKeyPress={(e) => e.key === "Enter" && lookupIP()} placeholder="Enter IP address (e.g., 8.8.8.8)" className="input-field pl-12" />
          </div>
          <button data-testid="lookup-ip-btn" onClick={lookupIP} disabled={loading || !ip.trim()} className="btn-primary flex items-center gap-2 disabled:opacity-50">
            {loading ? <CircleNotch size={20} className="spinner" /> : <MagnifyingGlass size={20} />}
            Lookup
          </button>
        </div>
      </div>

      {result && (
        <div className="card p-6">
          <div className="grid grid-cols-2 gap-6">
            <div><h4 className="text-xs uppercase tracking-widest text-white/60 mb-2">Location</h4><p className="text-white text-lg">{result.city}, {result.country}</p></div>
            <div><h4 className="text-xs uppercase tracking-widest text-white/60 mb-2">ISP</h4><p className="text-white text-lg">{result.isp}</p></div>
            <div><h4 className="text-xs uppercase tracking-widest text-white/60 mb-2">Organization</h4><p className="text-white text-lg">{result.org}</p></div>
            <div><h4 className="text-xs uppercase tracking-widest text-white/60 mb-2">Threat Level</h4><ThreatBadge level={result.threat_level} /></div>
          </div>
          <div className="mt-6 flex gap-4">
            {result.is_proxy && <span className="badge bg-[#FF9500]/20 text-[#FF9500]">PROXY</span>}
            {result.is_vpn && <span className="badge bg-[#FF9500]/20 text-[#FF9500]">VPN</span>}
            {result.is_tor && <span className="badge bg-[#FF3B30]/20 text-[#FF3B30]">TOR</span>}
            {result.is_datacenter && <span className="badge bg-[#5856D6]/20 text-[#5856D6]">DATACENTER</span>}
          </div>
        </div>
      )}
    </div>
  );
};

// Link Expander Component
const LinkExpander = () => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const expandLink = async () => {
    if (!url.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/security/expand-link`, { short_url: url });
      setResult(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="link-expander-view" className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading font-black text-3xl text-white mb-2">Link Expander</h2>
        <p className="text-white/60">Reveal the real destination of shortened URLs</p>
      </div>

      <div className="card p-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <LinkBreak size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input type="text" data-testid="short-url-input" value={url} onChange={(e) => setUrl(e.target.value)} onKeyPress={(e) => e.key === "Enter" && expandLink()} placeholder="Enter shortened URL (bit.ly, tinyurl, etc.)" className="input-field pl-12" />
          </div>
          <button data-testid="expand-link-btn" onClick={expandLink} disabled={loading || !url.trim()} className="btn-primary flex items-center gap-2 disabled:opacity-50">
            {loading ? <CircleNotch size={20} className="spinner" /> : <LinkIcon size={20} />}
            Expand
          </button>
        </div>
      </div>

      {result && (
        <div className="card p-6">
          <div className="space-y-4">
            <div><h4 className="text-xs uppercase tracking-widest text-white/60 mb-2">Original URL</h4><p className="text-white/60 font-mono text-sm break-all">{result.original_url}</p></div>
            <div><h4 className="text-xs uppercase tracking-widest text-white/60 mb-2">Final Destination</h4><p className="text-white font-mono text-sm break-all">{result.final_url}</p></div>
            <div><h4 className="text-xs uppercase tracking-widest text-white/60 mb-2">Redirects: {result.redirect_count}</h4></div>
          </div>
          {result.warning && (
            <div className="mt-4 p-4 rounded-lg bg-[#FF3B30]/10 border border-[#FF3B30]">
              <div className="flex items-center gap-3"><Warning size={24} className="text-[#FF3B30]" /><span className="text-[#FF3B30]">{result.warning}</span></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Hash Lookup Component
const HashLookup = () => {
  const [hash, setHash] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const lookupHash = async () => {
    if (!hash.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/security/hash-lookup`, { hash_value: hash });
      setResult(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="hash-lookup-view" className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading font-black text-3xl text-white mb-2">Hash Lookup</h2>
        <p className="text-white/60">Check if a file hash is known malware (MD5, SHA-1, SHA-256)</p>
      </div>

      <div className="card p-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Hash size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input type="text" data-testid="hash-input" value={hash} onChange={(e) => setHash(e.target.value)} onKeyPress={(e) => e.key === "Enter" && lookupHash()} placeholder="Enter file hash (MD5, SHA-1, or SHA-256)" className="input-field pl-12 font-mono" />
          </div>
          <button data-testid="lookup-hash-btn" onClick={lookupHash} disabled={loading || !hash.trim()} className="btn-primary flex items-center gap-2 disabled:opacity-50">
            {loading ? <CircleNotch size={20} className="spinner" /> : <MagnifyingGlass size={20} />}
            Lookup
          </button>
        </div>
      </div>

      {result && (
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className={`p-4 rounded-lg ${result.found && result.malicious_count > 0 ? "bg-[#FF3B30]/20" : "bg-[#39FF14]/20"}`}>
              {result.found && result.malicious_count > 0 ? <ShieldWarning size={32} className="text-[#FF3B30]" /> : <ShieldCheck size={32} className="text-[#39FF14]" />}
            </div>
            <div>
              <h3 className="font-heading font-bold text-xl text-white">{result.found ? (result.malicious_count > 0 ? "Malware Detected" : "File is Clean") : "Hash Not Found"}</h3>
              <p className="text-white/60">{result.hash_type} Hash</p>
            </div>
            <div className="ml-auto"><ThreatBadge level={result.threat_level} /></div>
          </div>

          {result.found && (
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-4 bg-[#FF3B30]/10 rounded-lg"><p className="text-2xl font-bold text-[#FF3B30]">{result.malicious_count}</p><p className="text-xs text-white/60 uppercase">Malicious</p></div>
              <div className="text-center p-4 bg-[#FFCC00]/10 rounded-lg"><p className="text-2xl font-bold text-[#FFCC00]">{result.suspicious_count}</p><p className="text-xs text-white/60 uppercase">Suspicious</p></div>
              <div className="text-center p-4 bg-[#39FF14]/10 rounded-lg"><p className="text-2xl font-bold text-[#39FF14]">{result.harmless_count}</p><p className="text-xs text-white/60 uppercase">Harmless</p></div>
              <div className="text-center p-4 bg-white/5 rounded-lg"><p className="text-2xl font-bold text-white/60">{result.undetected_count}</p><p className="text-xs text-white/60 uppercase">Undetected</p></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Email Analyzer Component
const EmailAnalyzer = () => {
  const [headers, setHeaders] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const analyzeHeaders = async () => {
    if (!headers.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/security/analyze-email-headers`, { headers });
      setResult(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="email-analyzer-view" className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading font-black text-3xl text-white mb-2">Email Header Analyzer</h2>
        <p className="text-white/60">Analyze email headers to detect phishing and spoofing</p>
      </div>

      <div className="card p-6">
        <textarea data-testid="email-headers-input" value={headers} onChange={(e) => setHeaders(e.target.value)} placeholder="Paste email headers here..." className="input-field h-40 font-mono text-sm resize-none" />
        <button data-testid="analyze-headers-btn" onClick={analyzeHeaders} disabled={loading || !headers.trim()} className="btn-primary mt-4 flex items-center gap-2 disabled:opacity-50">
          {loading ? <CircleNotch size={20} className="spinner" /> : <EnvelopeSimple size={20} />}
          Analyze Headers
        </button>
      </div>

      {result && (
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className={`p-4 rounded-lg ${result.is_suspicious ? "bg-[#FF3B30]/20" : "bg-[#39FF14]/20"}`}>
              {result.is_suspicious ? <ShieldWarning size={32} className="text-[#FF3B30]" /> : <ShieldCheck size={32} className="text-[#39FF14]" />}
            </div>
            <div>
              <h3 className="font-heading font-bold text-xl text-white">{result.is_suspicious ? "Suspicious Email" : "Email Looks Safe"}</h3>
              <p className="text-white/60">Risk Score: {result.risk_score}/100</p>
            </div>
          </div>

          <div className="flex gap-4 mb-6">
            <span className={`badge ${result.spf_result === "pass" ? "bg-[#39FF14]/20 text-[#39FF14]" : result.spf_result === "fail" ? "bg-[#FF3B30]/20 text-[#FF3B30]" : "bg-white/10 text-white/60"}`}>SPF: {result.spf_result?.toUpperCase()}</span>
            <span className={`badge ${result.dkim_result === "pass" ? "bg-[#39FF14]/20 text-[#39FF14]" : result.dkim_result === "fail" ? "bg-[#FF3B30]/20 text-[#FF3B30]" : "bg-white/10 text-white/60"}`}>DKIM: {result.dkim_result?.toUpperCase()}</span>
            <span className={`badge ${result.dmarc_result === "pass" ? "bg-[#39FF14]/20 text-[#39FF14]" : result.dmarc_result === "fail" ? "bg-[#FF3B30]/20 text-[#FF3B30]" : "bg-white/10 text-white/60"}`}>DMARC: {result.dmarc_result?.toUpperCase()}</span>
          </div>

          {result.warnings?.length > 0 && (
            <div>
              <h4 className="font-bold text-white mb-3">Warnings</h4>
              <ul className="space-y-2">
                {result.warnings.map((warning, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-[#FF3B30]"><Warning size={16} className="mt-1 flex-shrink-0" />{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// QR Scanner Component
const QRScanner = () => {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setScanning(true);
    setResult(null);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/security/scan-qrcode`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to scan QR code");
    } finally {
      setScanning(false);
    }
  };

  return (
    <div data-testid="qr-scanner-view" className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading font-black text-3xl text-white mb-2">QR Code Scanner</h2>
        <p className="text-white/60">Upload a QR code image to check if the URL is safe</p>
      </div>

      <div className="card p-6">
        <input type="file" id="qr-input" data-testid="qr-input" className="hidden" accept="image/*" onChange={handleFileSelect} disabled={scanning} />
        <label htmlFor="qr-input" className="dropzone p-12 text-center block cursor-pointer">
          {scanning ? (
            <div className="space-y-4"><CircleNotch size={64} className="spinner text-[#007AFF] mx-auto" /><p className="text-white text-lg">Scanning QR code...</p></div>
          ) : (
            <><QrCode size={64} className="text-white/40 mx-auto mb-4" /><p className="text-white text-lg mb-2">Click to upload QR code image</p><p className="text-white/40 text-sm">Supports PNG, JPG, JPEG</p></>
          )}
        </label>
      </div>

      {error && (
        <div className="card p-4 border-[#FF3B30] bg-[#FF3B30]/10">
          <div className="flex items-center gap-3"><XCircle size={24} className="text-[#FF3B30]" /><span className="text-[#FF3B30]">{error}</span></div>
        </div>
      )}

      {result && (
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className={`p-4 rounded-lg ${result.url_safe === false ? "bg-[#FF3B30]/20" : "bg-[#39FF14]/20"}`}>
              {result.url_safe === false ? <ShieldWarning size={32} className="text-[#FF3B30]" /> : <ShieldCheck size={32} className="text-[#39FF14]" />}
            </div>
            <div>
              <h3 className="font-heading font-bold text-xl text-white capitalize">{result.content_type} QR Code</h3>
              <p className="text-white/60">{result.is_url ? (result.url_safe ? "URL appears safe" : "URL may be dangerous") : "Non-URL content"}</p>
            </div>
          </div>
          <div className="mb-4">
            <h4 className="text-xs uppercase tracking-widest text-white/60 mb-2">Content</h4>
            <p className="text-white font-mono text-sm break-all bg-[#1A1A1A] p-3 rounded">{result.qr_content}</p>
          </div>
          {result.warnings?.length > 0 && (
            <ul className="space-y-2">
              {result.warnings.map((warning, idx) => (
                <li key={idx} className="flex items-start gap-2 text-[#FF3B30]"><Warning size={16} className="mt-1 flex-shrink-0" />{warning}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// Scan History Component
const ScanHistory = ({ refreshTrigger }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScan, setSelectedScan] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/scan/history`);
      setHistory(response.data.scans || []);
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory, refreshTrigger]);

  const deleteScan = async (scanId) => {
    try {
      await axios.delete(`${API}/scan/${scanId}`);
      setHistory(history.filter((s) => s.id !== scanId));
      if (selectedScan?.id === scanId) setSelectedScan(null);
    } catch (err) {
      console.error("Failed to delete scan:", err);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><CircleNotch size={48} className="spinner text-[#007AFF]" /></div>;

  return (
    <div data-testid="scan-history-view" className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading font-black text-3xl text-white mb-2">Scan History</h2>
        <p className="text-white/60">View all previous security scans</p>
      </div>

      {history.length === 0 ? (
        <div className="card p-12 text-center">
          <ClockCounterClockwise size={64} className="text-white/20 mx-auto mb-4" />
          <p className="text-white/60">No scan history yet</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="data-table">
            <thead><tr><th>Type</th><th>Target</th><th>Threat Level</th><th>Detections</th><th>Date</th><th>Actions</th></tr></thead>
            <tbody>
              {history.map((scan) => (
                <tr key={scan.id}>
                  <td>{scan.scan_type === "file" ? <File size={20} className="text-white/60" /> : <Globe size={20} className="text-white/60" />}</td>
                  <td className="max-w-[200px] truncate">{scan.target}</td>
                  <td><ThreatBadge level={scan.threat_level} /></td>
                  <td><span className="text-[#FF3B30]">{scan.malicious_count || 0}</span><span className="text-white/40"> / </span><span className="text-white/60">{(scan.malicious_count || 0) + (scan.harmless_count || 0) + (scan.suspicious_count || 0) + (scan.undetected_count || 0)}</span></td>
                  <td className="text-white/60 text-sm">{new Date(scan.timestamp).toLocaleDateString()}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button data-testid={`view-scan-${scan.id}`} onClick={() => setSelectedScan(scan)} className="p-2 hover:bg-white/10 rounded transition-colors"><CaretRight size={16} className="text-white/60" /></button>
                      <button data-testid={`delete-scan-${scan.id}`} onClick={() => deleteScan(scan.id)} className="p-2 hover:bg-[#FF3B30]/20 rounded transition-colors"><Trash size={16} className="text-[#FF3B30]" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedScan && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="mb-4 flex justify-end"><button data-testid="close-scan-detail" onClick={() => setSelectedScan(null)} className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"><XCircle size={24} className="text-white" /></button></div>
            <ScanResultCard result={selectedScan} />
          </div>
        </div>
      )}
    </div>
  );
};

// Security Tips Component
const SecurityTips = () => {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTips = async () => {
      try {
        const response = await axios.get(`${API}/security-tips`);
        setTips(response.data.tips || []);
      } catch (err) {
        console.error("Failed to fetch tips:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTips();
  }, []);

  const priorityColors = { high: "#FF3B30", medium: "#FFCC00", low: "#39FF14" };
  const categoryIcons = { System: Shield, Authentication: ShieldCheck, Email: EnvelopeSimple, Browsing: Globe, Data: File, Network: LinkIcon, Privacy: ShieldWarning, Monitoring: ClockCounterClockwise };

  if (loading) return <div className="flex items-center justify-center h-64"><CircleNotch size={48} className="spinner text-[#007AFF]" /></div>;

  return (
    <div data-testid="security-tips-view" className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading font-black text-3xl text-white mb-2">Security Tips</h2>
        <p className="text-white/60">Best practices to keep your system safe</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tips.map((tip, idx) => {
          const Icon = categoryIcons[tip.category] || Info;
          return (
            <div key={tip.id} className={`card p-6 animate-fadeIn stagger-${(idx % 4) + 1}`} style={{ animationFillMode: "both" }}>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: `${priorityColors[tip.priority]}20` }}><Icon size={24} style={{ color: priorityColors[tip.priority] }} /></div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-bold text-white">{tip.title}</h3>
                    <span className="badge text-[10px]" style={{ backgroundColor: `${priorityColors[tip.priority]}20`, color: priorityColors[tip.priority] }}>{tip.priority}</span>
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed">{tip.description}</p>
                  <p className="text-white/40 text-xs mt-2 uppercase tracking-wider">{tip.category}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const [accessState, setAccessState] = useState(() => {
    const stored = sessionStorage.getItem("av_access");
    return stored ? JSON.parse(stored) : null;
  });
  const [downloadType, setDownloadType] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { 
    if (accessState && downloadType) fetchStats(); 
  }, [fetchStats, refreshTrigger, accessState, downloadType]);

  const handleAccessGranted = (role, code) => {
    setAccessState({ role, code });
  };

  const handleLogout = () => {
    sessionStorage.removeItem("av_access");
    localStorage.removeItem("av_device_id");
    setAccessState(null);
    setDownloadType(null);
  };

  const handleScanComplete = () => setRefreshTrigger((prev) => prev + 1);

  // Not authenticated - show access gate
  if (!accessState) {
    return <AccessGate onAccessGranted={handleAccessGranted} />;
  }

  // Admin - show admin dashboard
  if (accessState.role === "admin") {
    return <AdminDashboard adminCode={accessState.code} onLogout={handleLogout} />;
  }

  // User authenticated but hasn't selected download type
  if (!downloadType) {
    return <DownloadOptions onSelect={setDownloadType} onBack={handleLogout} />;
  }

  // Regular user - show scanner app
  const renderContent = () => {
    switch (activeTab) {
      case "dashboard": return <Dashboard stats={stats} loading={loading} />;
      case "file-scan": return <FileScanner onScanComplete={handleScanComplete} />;
      case "url-scan": return <URLScanner onScanComplete={handleScanComplete} />;
      case "password-check": return <PasswordChecker />;
      case "ip-lookup": return <IPLookup />;
      case "link-expander": return <LinkExpander />;
      case "hash-lookup": return <HashLookup />;
      case "email-analyzer": return <EmailAnalyzer />;
      case "qr-scanner": return <QRScanner />;
      case "history": return <ScanHistory refreshTrigger={refreshTrigger} />;
      case "tips": return <SecurityTips />;
      default: return <Dashboard stats={stats} loading={loading} />;
    }
  };

  return (
    <div className="App min-h-screen bg-[#0A0A0A]">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} onLogout={handleLogout} />
      <main className="ml-64 p-8">{renderContent()}</main>
    </div>
  );
}

export default App;
